import type { MouseEvent } from 'react';
import type { Conversation } from '../../../../lib/conversations';
import {
  briefRowInMotion,
  briefRowTitle,
  conversationDisplayTitle,
  formatConversationDate,
  isBriefConversation,
} from '../../../../lib/conversation-display-title';
import { useTranslation } from '../../../../lib/i18n';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { useConversationSwitch } from './use-conversation-switch';

/**
 * How a conversation-list row is NAMED and OPENED — one place, shared by every list that reads the
 * conversation cache (the sidebar's recent/pinned sections and the header search dialog). The cache carries
 * BRIEF rows alongside chats, so a list that names or opens rows its own way gets a brief wrong: "Untitled"
 * for an unnamed brief, and a persisted switch that FloatingChat's latest-brief repair bounces back off.
 */

/** The row title: a brief (source=BRIEF) is named the way the phones' drawer names it — its topic, else the
 *  brief label (with "· In progress" while it convenes); web's `brief.title` carries a {time} slot, filled
 *  with the brief's start so two unnamed briefs are still told apart. A chat is named by
 *  `conversationDisplayTitle` (its name, else its start, else "Untitled"). */
export function useConversationRowTitle(): (conv: Conversation) => string {
  const { t } = useTranslation();
  return (conv) =>
    isBriefConversation(conv)
      ? briefRowTitle(conv.name, briefRowInMotion(conv), t('brief.title', { time: formatConversationDate(conv.createdDate) }), t('brief.inProgress'))
      : conversationDisplayTitle(conv.name, conv.createdDate, t('common.untitled') || '');
}

/** Opens a row. A brief opens the way the home's brief does — as a PEEK: shown, but never written into the
 *  resumable pointer or the URL, and closing the chat returns to the last real chat. That is what the phones
 *  do for a brief row (iOS `openHistoryConversation` / Android's drawer pass `source: "BRIEF"`, which keeps
 *  it out of the persisted last-conversation pointer). It also keeps FloatingChat's "the brief leaked into
 *  the active pointer" repair from bouncing the user off the latest brief the moment they open it. Every
 *  other row swaps in place via `useConversationSwitch`. */
export function useOpenConversationRow(): (conv: Pick<Conversation, 'id' | 'source'>, e: MouseEvent) => void {
  const switchConversation = useConversationSwitch();
  const { openBriefConversation } = useActiveConversation();
  return (conv, e) => {
    if (isBriefConversation(conv) && conv.id) {
      e.preventDefault();
      openBriefConversation(conv.id);
      return;
    }
    switchConversation(conv.id, e);
  };
}
